/**
 * ClutchTimeModal.js
 *
 * Entry modal for starting Clutch Time.
 * Multi-step flow:
 * 1. Enter test subject and date (or continue saved prep)
 * 2. Paste study guide text (optional)
 * 3. Confirm extracted topics
 *
 * MVP Implementation - December 2025
 * Phase 1.5 - Study Guide Import
 * Phase 3 - FERPA-Compliant Prep Persistence
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const ClutchTimeModal = ({
  isOpen,
  onClose,
  onStart,
  prefilledSubject = '',
  prefilledDate = ''
}) => {
  // Step management: 'subject' | 'paste' | 'confirm'
  const [step, setStep] = useState('subject');

  // Form state
  const [subject, setSubject] = useState(prefilledSubject);
  const [testDate, setTestDate] = useState(prefilledDate);
  const [subjects, setSubjects] = useState([]);

  // Saved preps for "Continue" feature
  const [savedPreps, setSavedPreps] = useState([]);
  const [loadingPreps, setLoadingPreps] = useState(true);

  // Study guide import state
  const [importMode, setImportMode] = useState('paste'); // 'paste' | 'google_doc' | 'pdf'
  const [studyGuideText, setStudyGuideText] = useState('');
  const [googleDocUrl, setGoogleDocUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState(null);
  const fileInputRef = useRef(null);

  // Topic confirmation state
  const [extractedTopics, setExtractedTopics] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState(new Set());
  const [newTopicInput, setNewTopicInput] = useState('');
  const [detectedSubject, setDetectedSubject] = useState('');
  const [importSource, setImportSource] = useState('manual');

  const [loading, setLoading] = useState(false);

  // Fetch available subjects and saved preps on mount
  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const response = await axios.get('/api/clutch-time/subjects', {
          withCredentials: true
        });
        if (response.data.success) {
          setSubjects(response.data.subjects || []);
        }
      } catch (err) {
        console.error('Error fetching subjects:', err);
      }
    };

    const fetchSavedPreps = async () => {
      setLoadingPreps(true);
      try {
        const response = await axios.get('/api/clutch-time/prep/active', {
          withCredentials: true
        });
        if (response.data.success) {
          setSavedPreps(response.data.preps || []);
        }
      } catch (err) {
        console.error('Error fetching saved preps:', err);
      } finally {
        setLoadingPreps(false);
      }
    };

    if (isOpen) {
      fetchSubjects();
      fetchSavedPreps();
    }
  }, [isOpen]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('subject');
      setSubject(prefilledSubject);
      setTestDate(prefilledDate);
      setImportMode('paste');
      setStudyGuideText('');
      setGoogleDocUrl('');
      setSelectedFile(null);
      setDragActive(false);
      setExtractedTopics([]);
      setSelectedTopics(new Set());
      setExtractError(null);
      setNewTopicInput('');
      setDetectedSubject('');
      setImportSource('manual');
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isOpen, prefilledSubject, prefilledDate]);

  // Continue from a saved prep
  const handleContinuePrep = (prep) => {
    // Convert saved topics to expected format
    const topics = (prep.topics || []).map((t, idx) => ({
      name: t.name,
      detected_context: t.context,
      covered: t.covered || false
    }));

    setLoading(true);
    onStart({
      subject: prep.subject,
      testDate: prep.test_date ? prep.test_date.split('T')[0] : null,
      importedTopics: topics,
      importSource: prep.source_type || 'saved',
      prepId: prep.id  // Pass prep ID for progress tracking
    });
  };

  // Format time since last studied
  const formatTimeSince = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) return 'just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'yesterday';
    return `${diffDays}d ago`;
  };

  // Format test date for display
  const formatTestDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays < 0) return 'Past';
    if (diffDays <= 7) return `in ${diffDays}d`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Handle subject/date submission
  const handleSubjectSubmit = (e) => {
    e.preventDefault();
    if (!subject.trim()) return;
    setStep('paste');
  };

  // Handle study guide extraction (paste text mode)
  const handleExtractTopics = async () => {
    if (!studyGuideText.trim()) {
      // Skip to radar with no extracted topics
      handleStartWithoutTopics();
      return;
    }

    setExtracting(true);
    setExtractError(null);

    try {
      const response = await axios.post('/api/clutch-time/extract-topics', {
        text: studyGuideText,
        subject: subject
      }, {
        withCredentials: true
      });

      if (response.data.success) {
        const topics = response.data.topics || [];
        setExtractedTopics(topics);
        // Select all topics by default
        setSelectedTopics(new Set(topics.map((_, idx) => idx)));
        setDetectedSubject(response.data.subject_detected || subject);
        setImportSource('paste_text');
        setStep('confirm');
      } else {
        setExtractError(response.data.error || 'Failed to extract topics');
      }
    } catch (err) {
      console.error('Error extracting topics:', err);
      setExtractError(err.response?.data?.error || 'Failed to extract topics. Please try again.');
    } finally {
      setExtracting(false);
    }
  };

  // Handle Google Doc/Drive import
  const handleImportGoogleDoc = async () => {
    if (!googleDocUrl.trim()) {
      setExtractError('Please enter a Google Doc or Drive URL');
      return;
    }

    // Basic URL validation - accept both Google Docs and Drive URLs
    const isGoogleUrl = googleDocUrl.includes('docs.google.com') || googleDocUrl.includes('drive.google.com');
    if (!isGoogleUrl) {
      setExtractError('Please enter a valid Google Docs or Google Drive URL');
      return;
    }

    setExtracting(true);
    setExtractError(null);

    try {
      const response = await axios.post('/api/clutch-time/import-google-doc', {
        url: googleDocUrl,
        subject: subject
      }, {
        withCredentials: true
      });

      if (response.data.success) {
        const topics = response.data.topics || [];
        setExtractedTopics(topics);
        // Select all topics by default
        setSelectedTopics(new Set(topics.map((_, idx) => idx)));
        setDetectedSubject(response.data.subject_detected || subject);
        setImportSource('google_doc');
        setStep('confirm');
      } else {
        setExtractError(response.data.error || 'Failed to import document');
      }
    } catch (err) {
      console.error('Error importing Google Doc:', err);
      setExtractError(err.response?.data?.error || 'Failed to import document. Make sure it\'s shared publicly.');
    } finally {
      setExtracting(false);
    }
  };

  // Handle PDF file selection
  const handleFileSelect = (file) => {
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setExtractError('Only PDF files are supported');
      setSelectedFile(null);
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setExtractError('File too large. Maximum size is 10MB.');
      setSelectedFile(null);
      return;
    }

    setExtractError(null);
    setSelectedFile(file);
  };

  // Handle PDF upload
  const handleUploadPdf = async () => {
    if (!selectedFile) {
      setExtractError('Please select a PDF file');
      return;
    }

    setExtracting(true);
    setExtractError(null);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('subject', subject);

    try {
      const response = await axios.post('/api/clutch-time/upload-pdf', formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const topics = response.data.topics || [];
        setExtractedTopics(topics);
        setSelectedTopics(new Set(topics.map((_, idx) => idx)));
        setDetectedSubject(response.data.subject_detected || subject);
        setImportSource('pdf_upload');
        setStep('confirm');
      } else {
        setExtractError(response.data.error || 'Failed to process PDF');
      }
    } catch (err) {
      console.error('Error uploading PDF:', err);
      setExtractError(err.response?.data?.error || 'Failed to process PDF. Please try again.');
    } finally {
      setExtracting(false);
    }
  };

  // Drag and drop handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  }, []);

  const handleDragOut = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Skip paste step
  const handleSkipPaste = () => {
    handleStartWithoutTopics();
  };

  // Start without extracted topics
  const handleStartWithoutTopics = () => {
    setLoading(true);
    onStart({
      subject: subject.trim(),
      testDate: testDate || null,
      importedTopics: [],
      importSource: 'manual'
    });
  };

  // Toggle topic selection
  const toggleTopic = useCallback((index) => {
    setSelectedTopics(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  // Add custom topic
  const handleAddTopic = () => {
    if (!newTopicInput.trim()) return;

    const newTopic = {
      name: newTopicInput.trim(),
      detected_context: null,
      isCustom: true
    };

    setExtractedTopics(prev => [...prev, newTopic]);
    setSelectedTopics(prev => new Set([...prev, extractedTopics.length]));
    setNewTopicInput('');
  };

  // Start with selected topics (saves prep for persistence)
  const handleStartWithTopics = async () => {
    const selectedTopicsList = extractedTopics
      .filter((_, idx) => selectedTopics.has(idx))
      .map(t => ({ name: t.name, detected_context: t.detected_context }));

    setLoading(true);

    // Save prep to database for persistence (FERPA-compliant)
    try {
      const saveResponse = await axios.post('/api/clutch-time/prep/save', {
        subject: detectedSubject || subject.trim(),
        test_date: testDate || null,
        topics: selectedTopicsList,
        source_type: importSource,
        source_filename: selectedFile?.name || null
      }, { withCredentials: true });

      if (saveResponse.data.success) {
        // Pass prep ID for progress tracking
        onStart({
          subject: detectedSubject || subject.trim(),
          testDate: testDate || null,
          importedTopics: selectedTopicsList,
          importSource: importSource,
          prepId: saveResponse.data.prep_id
        });
      } else {
        // Even if save fails, still start the session
        console.warn('Failed to save prep, continuing anyway');
        onStart({
          subject: detectedSubject || subject.trim(),
          testDate: testDate || null,
          importedTopics: selectedTopicsList,
          importSource: importSource
        });
      }
    } catch (err) {
      console.error('Error saving prep:', err);
      // Start session even if save fails
      onStart({
        subject: detectedSubject || subject.trim(),
        testDate: testDate || null,
        importedTopics: selectedTopicsList,
        importSource: importSource
      });
    } finally {
      setLoading(false);
    }
  };

  // Quick date buttons
  const quickDates = [
    { label: 'Today', getValue: () => new Date().toISOString().split('T')[0] },
    { label: 'Tomorrow', getValue: () => {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      return d.toISOString().split('T')[0];
    }},
    { label: 'This Week', getValue: () => {
      const d = new Date();
      d.setDate(d.getDate() + 5);
      return d.toISOString().split('T')[0];
    }}
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="clutch-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="clutch-modal clutch-modal-expanded"
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="clutch-modal-header">
            <div className="clutch-modal-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
              </svg>
            </div>
            <h2>Clutch Time</h2>
            <button className="clutch-modal-close" onClick={onClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          {/* Step Indicator */}
          <div className="clutch-step-indicator">
            <div className={`clutch-step ${step === 'subject' ? 'active' : 'completed'}`}>
              <span className="clutch-step-number">1</span>
              <span className="clutch-step-label">Test Info</span>
            </div>
            <div className="clutch-step-divider" />
            <div className={`clutch-step ${step === 'paste' ? 'active' : step === 'confirm' ? 'completed' : ''}`}>
              <span className="clutch-step-number">2</span>
              <span className="clutch-step-label">Study Guide</span>
            </div>
            <div className="clutch-step-divider" />
            <div className={`clutch-step ${step === 'confirm' ? 'active' : ''}`}>
              <span className="clutch-step-number">3</span>
              <span className="clutch-step-label">Topics</span>
            </div>
          </div>

          {/* Step 1: Subject & Date */}
          {step === 'subject' && (
            <form onSubmit={handleSubjectSubmit} className="clutch-modal-form">
              {/* Continue Where You Left Off - Gen Z friendly */}
              {!loadingPreps && savedPreps.length > 0 && (
                <div className="clutch-continue-section">
                  <div className="clutch-continue-header">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22,4 12,14.01 9,11.01"/>
                    </svg>
                    <span>Continue where you left off</span>
                  </div>
                  <div className="clutch-continue-cards">
                    {savedPreps.slice(0, 2).map((prep) => (
                      <button
                        key={prep.id}
                        type="button"
                        className="clutch-continue-card"
                        onClick={() => handleContinuePrep(prep)}
                      >
                        <div className="clutch-continue-subject">{prep.subject}</div>
                        <div className="clutch-continue-meta">
                          {prep.test_date && (
                            <span className="clutch-continue-date">
                              {formatTestDate(prep.test_date)}
                            </span>
                          )}
                          <span className="clutch-continue-progress">
                            {prep.topics_covered}/{prep.total_topics} topics
                          </span>
                        </div>
                        {prep.last_studied_at && (
                          <div className="clutch-continue-last">
                            {formatTimeSince(prep.last_studied_at)}
                          </div>
                        )}
                        <svg className="clutch-continue-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 18l6-6-6-6"/>
                        </svg>
                      </button>
                    ))}
                  </div>
                  <div className="clutch-or-divider">
                    <span>or start new</span>
                  </div>
                </div>
              )}

              <div className="clutch-form-group">
                <label htmlFor="subject">What test are you studying for?</label>
                <input
                  id="subject"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g., Biology, Math, History..."
                  autoFocus={savedPreps.length === 0}
                  list="subject-suggestions"
                />
                {subjects.length > 0 && (
                  <datalist id="subject-suggestions">
                    {subjects.map((s, idx) => (
                      <option key={idx} value={s} />
                    ))}
                  </datalist>
                )}
              </div>

              <div className="clutch-form-group">
                <label htmlFor="testDate">When is the test? (optional)</label>
                <input
                  id="testDate"
                  type="date"
                  value={testDate}
                  onChange={(e) => setTestDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
                <div className="clutch-quick-dates">
                  {quickDates.map((qd, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={`clutch-quick-date ${testDate === qd.getValue() ? 'active' : ''}`}
                      onClick={() => setTestDate(qd.getValue())}
                    >
                      {qd.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="clutch-modal-actions">
                <button
                  type="submit"
                  className="clutch-modal-submit"
                  disabled={!subject.trim()}
                >
                  Next: Add Study Guide
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 18l6-6-6-6"/>
                  </svg>
                </button>
              </div>
            </form>
          )}

          {/* Step 2: Import Study Guide */}
          {step === 'paste' && (
            <div className="clutch-modal-form">
              {/* Import Mode Tabs */}
              <div className="clutch-import-tabs">
                <button
                  type="button"
                  className={`clutch-import-tab ${importMode === 'paste' ? 'active' : ''}`}
                  onClick={() => { setImportMode('paste'); setExtractError(null); }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                    <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
                  </svg>
                  Paste
                </button>
                <button
                  type="button"
                  className={`clutch-import-tab ${importMode === 'google_doc' ? 'active' : ''}`}
                  onClick={() => { setImportMode('google_doc'); setExtractError(null); }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                  </svg>
                  Doc
                </button>
                <button
                  type="button"
                  className={`clutch-import-tab ${importMode === 'pdf' ? 'active' : ''}`}
                  onClick={() => { setImportMode('pdf'); setExtractError(null); setSelectedFile(null); }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  PDF
                </button>
              </div>

              {/* Paste Text Mode */}
              {importMode === 'paste' && (
                <div className="clutch-form-group">
                  <label htmlFor="studyGuide">
                    Paste your study guide or notes
                    <span className="clutch-label-hint">(optional)</span>
                  </label>
                  <textarea
                    id="studyGuide"
                    className="clutch-textarea"
                    value={studyGuideText}
                    onChange={(e) => setStudyGuideText(e.target.value)}
                    placeholder="Paste your study guide, notes, or syllabus here...

Example:
Chapter 5: Cellular Respiration
- Glycolysis: occurs in cytoplasm
- Krebs Cycle: occurs in mitochondria
- Electron Transport Chain
- ATP production..."
                    rows={8}
                    autoFocus
                  />
                  <div className="clutch-char-count">
                    {studyGuideText.length > 0 && (
                      <span className={studyGuideText.length > 50000 ? 'error' : ''}>
                        {studyGuideText.length.toLocaleString()} / 50,000 characters
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Google Doc/Drive Mode */}
              {importMode === 'google_doc' && (
                <div className="clutch-form-group">
                  <label htmlFor="googleDocUrl">
                    Google Doc or Drive URL
                  </label>
                  <input
                    id="googleDocUrl"
                    type="url"
                    className="clutch-input"
                    value={googleDocUrl}
                    onChange={(e) => setGoogleDocUrl(e.target.value)}
                    placeholder="https://docs.google.com/... or https://drive.google.com/..."
                    autoFocus
                  />
                  <div className="clutch-google-doc-hint">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M12 16v-4"/>
                      <path d="M12 8h.01"/>
                    </svg>
                    Supports Google Docs and Drive PDFs. Set sharing to "Anyone with the link"
                  </div>
                </div>
              )}

              {/* PDF Upload Mode */}
              {importMode === 'pdf' && (
                <div className="clutch-form-group">
                  <label>Upload Study Guide PDF</label>
                  <div
                    className={`clutch-drop-zone ${dragActive ? 'active' : ''} ${selectedFile ? 'has-file' : ''}`}
                    onDragEnter={handleDragIn}
                    onDragLeave={handleDragOut}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {selectedFile ? (
                      <div className="clutch-selected-file">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                        </svg>
                        <div className="clutch-file-info">
                          <span className="clutch-file-name">{selectedFile.name}</span>
                          <span className="clutch-file-size">{formatFileSize(selectedFile.size)}</span>
                        </div>
                        <button
                          type="button"
                          className="clutch-remove-file"
                          onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="17 8 12 3 7 8"/>
                          <line x1="12" y1="3" x2="12" y2="15"/>
                        </svg>
                        <span className="clutch-drop-text">
                          {dragActive ? 'Drop PDF here' : 'Drag & drop or click to upload'}
                        </span>
                        <span className="clutch-drop-hint">PDF files only, max 10MB</span>
                      </>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    onChange={handleFileInputChange}
                    style={{ display: 'none' }}
                  />
                </div>
              )}

              {extractError && (
                <div className="clutch-error-message">
                  {extractError}
                </div>
              )}

              <div className="clutch-modal-actions clutch-actions-split">
                <button
                  type="button"
                  className="clutch-modal-secondary"
                  onClick={() => setStep('subject')}
                  disabled={extracting}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 18l-6-6 6-6"/>
                  </svg>
                  Back
                </button>

                <div className="clutch-actions-right">
                  <button
                    type="button"
                    className="clutch-modal-skip"
                    onClick={handleSkipPaste}
                    disabled={extracting}
                  >
                    Skip
                  </button>

                  {importMode === 'paste' && (
                    <button
                      type="button"
                      className={`clutch-modal-submit ${extracting ? 'clutch-btn-processing' : ''}`}
                      onClick={handleExtractTopics}
                      disabled={extracting || studyGuideText.length > 50000}
                    >
                      {extracting ? (
                        <span className="clutch-processing-content">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                          </svg>
                          Analyzing
                        </span>
                      ) : studyGuideText.trim() ? (
                        <>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                          </svg>
                          Extract Topics
                        </>
                      ) : (
                        <>
                          Start Studying
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 18l6-6-6-6"/>
                          </svg>
                        </>
                      )}
                    </button>
                  )}

                  {importMode === 'google_doc' && (
                    <button
                      type="button"
                      className={`clutch-modal-submit ${extracting ? 'clutch-btn-processing' : ''}`}
                      onClick={handleImportGoogleDoc}
                      disabled={extracting || !googleDocUrl.trim()}
                    >
                      {extracting ? (
                        <span className="clutch-processing-content">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                          </svg>
                          Importing
                        </span>
                      ) : (
                        <>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                          </svg>
                          Import Doc
                        </>
                      )}
                    </button>
                  )}

                  {importMode === 'pdf' && (
                    <button
                      type="button"
                      className={`clutch-modal-submit ${extracting ? 'clutch-btn-processing' : ''}`}
                      onClick={handleUploadPdf}
                      disabled={extracting || !selectedFile}
                    >
                      {extracting ? (
                        <span className="clutch-processing-content">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="17 8 12 3 7 8"/>
                            <line x1="12" y1="3" x2="12" y2="15"/>
                          </svg>
                          Processing
                        </span>
                      ) : (
                        <>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="17 8 12 3 7 8"/>
                            <line x1="12" y1="3" x2="12" y2="15"/>
                          </svg>
                          Extract Topics
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Confirm Topics */}
          {step === 'confirm' && (
            <div className="clutch-modal-form">
              <div className="clutch-topics-header">
                <div className="clutch-topics-title">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22,4 12,14.01 9,11.01"/>
                  </svg>
                  <span>Found {extractedTopics.length} topics</span>
                </div>
                {detectedSubject && detectedSubject !== subject && (
                  <div className="clutch-detected-subject">
                    Subject: <strong>{detectedSubject}</strong>
                  </div>
                )}
              </div>

              <div className="clutch-topics-list-confirm">
                {extractedTopics.map((topic, idx) => (
                  <label
                    key={idx}
                    className={`clutch-topic-checkbox ${selectedTopics.has(idx) ? 'selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTopics.has(idx)}
                      onChange={() => toggleTopic(idx)}
                    />
                    <span className="clutch-checkbox-custom" />
                    <div className="clutch-topic-content">
                      <span className="clutch-topic-name-confirm">{topic.name}</span>
                      {topic.detected_context && (
                        <span className="clutch-topic-context">{topic.detected_context}</span>
                      )}
                      {topic.isCustom && (
                        <span className="clutch-topic-custom-badge">Added by you</span>
                      )}
                    </div>
                  </label>
                ))}
              </div>

              <div className="clutch-add-topic">
                <input
                  type="text"
                  value={newTopicInput}
                  onChange={(e) => setNewTopicInput(e.target.value)}
                  placeholder="Add a topic I missed..."
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTopic()}
                />
                <button
                  type="button"
                  onClick={handleAddTopic}
                  disabled={!newTopicInput.trim()}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Add
                </button>
              </div>

              <p className="clutch-tip">
                Uncheck topics you already know well
              </p>

              <div className="clutch-modal-actions clutch-actions-split">
                <button
                  type="button"
                  className="clutch-modal-secondary"
                  onClick={() => setStep('paste')}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 18l-6-6 6-6"/>
                  </svg>
                  Back
                </button>

                <button
                  type="button"
                  className="clutch-modal-submit"
                  onClick={handleStartWithTopics}
                  disabled={selectedTopics.size === 0 || loading}
                >
                  {loading ? (
                    <span className="clutch-spinner-small" />
                  ) : (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                      </svg>
                      Start Studying ({selectedTopics.size} topics)
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ClutchTimeModal;
